import DexieDB from './DexieDB';
import DexieDBProvider from './DexieDBProvider';
import { DexieDataSetEvent } from '@/models/dexie/UnsyncedDexieDataSet';
import { DataSetEventStatusTypeEnums } from '@/enums/DataSetEventStatusTypeEnums';
import { OnlineService, DataSetService, AxiosService } from '.';
import { DataSetEventTypeEnums } from '@/enums/DataSetEventTypeEnums';
import { DataSet } from '@/models/dataset/DataSet';
import { DataSetSection } from '@/models/dataset/DataSetSection';
import { DataSetField } from '@/models/dataset/DataSetField';
import { EventStore } from '@/store/store.event';
import { ApiEndpoints } from '@/config/index';
import { AppSettingsStore } from '@/store/store.appSettings';
import { DataSetStore } from '@/store/store.dataSet';
import { LoggerService } from '@/utils';
import { OnlineStatusEnums } from '@/enums/OnlineStatusEnums';
import { AppSettingsService } from './index';
import { AuthStore } from '@/store/store.auth';
import { ApiIdentifers } from '@/config/index';

class EventsProvider {

    public lastSync?: Date;
    public syncTimer?: any;

    private dexieDB: DexieDB = DexieDBProvider.getInstance().dexieDB;

    // tslint:disable-next-line: no-empty
    constructor() {}

    /**
     * Get all unsynced events
     */
    public async GetAllUnsyncedEvents(): Promise<DexieDataSetEvent[]> {
        return this.dexieDB.getTable<DexieDataSetEvent>(this.dexieDB!.getTables().unsyncedEvents).toArray();
    }

    /**
     * Add a new unsynced avent
     * @param unsyncedEvent DexieDataSetEvent
     */
    public async AddUnsyncedEvent(unsyncedEvent: DexieDataSetEvent): Promise<number> {
        return this.dexieDB.add<DexieDataSetEvent>(this.dexieDB!.getTables().unsyncedEvents, unsyncedEvent);
    }

    /**
     * Put/Update an unsynced event
     * @param unsyncedEvent DexieDataSetEvent
     */
    public async PutUnsyncedEvent(unsyncedEvent: DexieDataSetEvent): Promise<number> {
        return this.dexieDB.put<DexieDataSetEvent>(this.dexieDB!.getTables().unsyncedEvents, unsyncedEvent);
    }

    /**
     * Put/Update a set of unsynced event
     * @param unsyncedEvent DexieDataSetEvent
     */
    public async PutUnsyncedEvents(unsyncedEvents: DexieDataSetEvent[]): Promise<number> {
        return this.dexieDB.bulkPut<DexieDataSetEvent>(this.dexieDB!.getTables().unsyncedEvents, unsyncedEvents);
    }

    /**
     * Delete an unsynced event
     * @param unsyncedEvent DexieDataSetEvent
     */
    public async DeleteUnsyncedEvent(unsyncedEvent: DexieDataSetEvent): Promise<void> {
        return this.dexieDB.del<DexieDataSetEvent>(this.dexieDB!.getTables().unsyncedEvents, unsyncedEvent.id);
    }
    /**
     * Delete an unsynced event by id
     * @param unsyncedEventId Id of DexieDataSetEvent
     */
    public async DeleteUnsyncedEventById(unsyncedEventId: number): Promise<void> {
        return this.dexieDB.del<DexieDataSetEvent>(this.dexieDB!.getTables().unsyncedEvents, unsyncedEventId);
    }

    public async DeleteAllUnsyncedEvents(): Promise<void> {
        return this.dexieDB.clear<DexieDataSetEvent>(this.dexieDB!.getTables().unsyncedEvents);
    }

    /**
     * Get all synced events
     */
    public async GetAllSyncedEvents(): Promise<DexieDataSetEvent[]> {
        return this.dexieDB.getTable<DexieDataSetEvent>(this.dexieDB!.getTables().syncedEvents).toArray();
    }

    /**
     * Add a new synced avent
     * @param syncedEvent DexieDataSetEvent
     */
    public async AddSyncedEvent(syncedEvent: DexieDataSetEvent): Promise<number> {
        return this.dexieDB.add<DexieDataSetEvent>(this.dexieDB!.getTables().syncedEvents, syncedEvent);
    }

    /**
     * Put/Update a synced event
     * @param syncedEvent DexieDataSetEvent
     */
    public async PutSyncedEvent(syncedEvent: DexieDataSetEvent): Promise<number> {
        return this.dexieDB.put<DexieDataSetEvent>(this.dexieDB!.getTables().syncedEvents, syncedEvent);
    }

    /**
     * Method to sync all unsynced events from local db to the server
     */
    public async SyncUnsyncedEvents() {
        await this.syncToServer()
            .then((data: DexieDataSetEvent[]) => {
                if (data.length > 0) {
                    LoggerService.LogWarn('Not all events synced.');
                } else {
                    LoggerService.LogWarn('All events synced with the api server.');
                }
            })
            .catch((error: any) => {
                LoggerService.LogError(`Error on sync all events: `, error);
            });
    }

    /**
     * Method to fetch all new evetents since the last fetch
     * @param sinceEventId Meth
     */
    public async SyncEventsFromServer(sinceEventId?: number): Promise<DexieDataSetEvent[]> {
        return new Promise(async (resolve, reject) => {
            if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE
                && AuthStore.GetIsLoggedIn === true) {
                await AxiosService.get(ApiEndpoints.eventsSince(sinceEventId), null)
                    .then((response: any) => {
                        const events: DexieDataSetEvent[] = [];
                        if (response.data) {
                            let lastEventId = 0;
                            response.data.forEach((event: any) => {
                                lastEventId = event && event.id || lastEventId;
                                events.push(new DexieDataSetEvent(event));
                            });
                            if (lastEventId > 0) {
                                // save highest evetn id to know where to append on next sync
                                AppSettingsService.ChangeLastEventId(lastEventId);
                            }
                        }
                        resolve(events);
                    })
                    .catch((error: any) => {
                        reject(error);
                    });
            } else {
                LoggerService.LogWarn(`App app seems to be offline`, OnlineService.GetOnlineStatus);
                reject(OnlineService.GetOnlineStatus);
            }
        });
    }


    public checkForAutosync(): Promise<any> {
        return new Promise(async (resolve, reject) => {
            if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE
                && AuthStore.GetIsLoggedIn === true) {
                await this.syncToServer()
                    .then((data: DexieDataSetEvent[]) => {
                        if (data.length > 0) {
                            for (const okEvent of data.filter((event) => event.eventStatus === DataSetEventStatusTypeEnums.OK)) {
                                // remove all synced events from dexiedb
                                this.DeleteUnsyncedEvent(okEvent);
                            }
                            // check if we have still unsynced events
                            data = data.filter((event) => event.eventStatus !== DataSetEventStatusTypeEnums.OK);
                            if (data.length > 0) {
                                LoggerService.LogDebug('Not all events auto-synced.');
                            } else {
                                LoggerService.LogDebug('All events auto-synced:', data);
                            }
                        }
                        EventStore.SetUnsyncedEvents(data)
                            .then((response) => {
                                this.lastSync = new Date();
                                resolve(response);
                            })
                            .catch((error: any) => {
                                reject(error);
                            });
                    })
                    .catch((error: any) => {
                        // LoggerService.LogError(`Error on auto-sync: `, error);
                        reject(error);
                    });
            } else {
                LoggerService.LogWarn(`App app seems to be offline`, OnlineStatusEnums[OnlineService.GetOnlineStatus]);
                reject(OnlineService.GetOnlineStatus);
            }
        });
    }

    public async deleteUnsyncedEventById(unsyncedEventId: number): Promise<any> {
        await this.DeleteUnsyncedEventById(unsyncedEventId);

        let events: DexieDataSetEvent[] = [];
        await this.GetAllUnsyncedEvents()
            .then((response: DexieDataSetEvent[]) => {
                events = response;
            });

        return new Promise<any>((resolve, reject) => {
            EventStore.SetUnsyncedEvents(events)
            .then((response) => {
                this.lastSync = new Date();
                resolve(response);
            })
            .catch((error: any) => {
                reject(error);
            });
        });
    }

    private async syncToServer(): Promise<DexieDataSetEvent[]> {
        let events: DexieDataSetEvent[] = [];
        const eventsToSend: DexieDataSetEvent[] = [];
        await this.GetAllUnsyncedEvents()
            .then((response: DexieDataSetEvent[]) => {
                events = response;
            });

        for (const event of events) {
            if (event.event === DataSetEventTypeEnums.UPDATE) {
                const oldData = DataSetService.GetAllDataSets().find((ds) => ds.id === event.dataSet.id);
                eventsToSend.push(this.filterUnsetFields(event, oldData));
            } else {
                eventsToSend.push(this.filterUnsetFields(event));
            }
        }

        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE
            && AuthStore.GetIsLoggedIn === true) {
            for (const event of eventsToSend) {
                let apiPath;
                switch (event.event) {
                    case DataSetEventTypeEnums.UPDATE:
                        apiPath = ApiEndpoints.eventsUpdate;
                        break;
                    case DataSetEventTypeEnums.DELETE:
                        apiPath = ApiEndpoints.eventsDelete;
                        break;
                    case DataSetEventTypeEnums.CREATE:
                    default:
                        apiPath = ApiEndpoints.eventsCreate;
                        break;
                }

                await AxiosService.post(apiPath, event)
                    .then((response) => {
                        const eventResponse = response.data;
                        event.eventStatus = DataSetEventStatusTypeEnums.OK;
                        event.eventStatusCode = response.status;
                    })
                    .catch((error) => {
                        event.eventStatus = DataSetEventStatusTypeEnums.ERROR_API;
                        event.eventStatusCode = error.response.status;
                        event.eventStatusInfo = error.request.responseText;
                    });
            }
        } else {
            LoggerService.LogWarn(`App app seems to be offline`, OnlineService.GetOnlineStatus);
        }

        return new Promise<DexieDataSetEvent[]>((resolve, reject) => {
            resolve(eventsToSend);
        });
    }

    private filterUnsetFields(inUnsyncedDataSet: DexieDataSetEvent, oldDataSet?: DataSet): DexieDataSetEvent {
        const udr = new DexieDataSetEvent();
        udr.date = inUnsyncedDataSet.date;
        udr.event = inUnsyncedDataSet.event;
        udr.eventStatus = inUnsyncedDataSet.eventStatus;
        udr.id = inUnsyncedDataSet.id;
        udr.dataSet = new DataSet();
        udr.dataSet.id = inUnsyncedDataSet.dataSet.id;
        udr.dataSet.dataSetId = inUnsyncedDataSet.dataSet.dataSetId;
        udr.dataSet.sections = [];

        for (const inSection of inUnsyncedDataSet.dataSet.sections) {
            const tmpSection = new DataSetSection();
            tmpSection.identifier = inSection.identifier;
            tmpSection.position = inSection.position;
            if (oldDataSet !== undefined) {
                const oldDataSetSection = oldDataSet.sections.find((dss) => dss.identifier === inSection.identifier);
                if (oldDataSetSection !== undefined && oldDataSetSection.fields !== undefined) {
                    if (inSection.identifier === ApiIdentifers.material) {
                        tmpSection.fields = EventsProvider.filterMaterialSectionFieldUpdates(
                            inSection.fields,
                            oldDataSetSection.fields
                        );
                    } else {
                        tmpSection.fields = inSection.fields.filter(EventsProvider.filterFieldUpdates(oldDataSetSection.fields));
                    }
                } else {
                    tmpSection.fields = inSection.fields.filter(EventsProvider.filterField);
                }

            } else {
                tmpSection.fields = inSection.fields.filter(EventsProvider.filterFieldNew);
            }

            if (tmpSection.fields.length > 0) {
                udr.dataSet.sections.push(tmpSection);
            }
        }
        return udr;
    }

    /**
     * Method to filter unset fields that should not be send to API.
     * In case someone just deleted/clear a field, we still need to send
     * it to API to clear the value in the saved data.
     * @param inField   Field to filter
     */
    // tslint:disable-next-line: member-ordering
    public static filterField(inField: DataSetField): any {
        if (inField.fields) {
            // return inField.fields.filter(EventsProvider.filterField, this);
            return (inField.fields = inField.fields.map((o) => Object.assign({}, o)).filter(EventsProvider.filterField)).length;
        } else if (inField.valueBool !== undefined || inField.valueNumber !== undefined || inField.valueString !== undefined) {
            return true;
        }
        return false;
    }

    /**
     * Method to check for emtpy fields on crate events.
     * Since in this case we need filter also fields that the suer filled and
     * deleted afterwards we need to also check for NaN and empty strings to filter
     * @param inField   Field to filter
     */
    // tslint:disable-next-line: member-ordering
    public static filterFieldNew(inField: DataSetField): any {
        if (inField.fields) {
            // return inField.fields.filter(EventsProvider.filterField, this);
            return (inField.fields = inField.fields.map((o) => Object.assign({}, o)).filter(EventsProvider.filterFieldNew)).length;
        } else if (inField.valueBool != null
            || (inField.valueNumber != null && !isNaN(inField.valueNumber))
            || (inField.valueString != null && inField.valueString.length > 0)) {
            return true;
        }
        return false;
    }

    /**
     * Material rows are repeatable subsections; when any row changes, send the full
     * remaining set so the API and later events/since stay consistent.
     */
    public static filterMaterialSectionFieldUpdates(
        inFields: DataSetField[],
        oldFields: DataSetField[]
    ): DataSetField[] {
        const hasChanges = inFields.some((inField) => {
            const probe = EventsProvider.cloneDataSetFieldDeep(inField);
            return !!EventsProvider.filterFieldUpdates(oldFields)(probe);
        });
        if (!hasChanges) {
            return [];
        }
        const structureBaseline = EventsProvider.getMaterialSubsectionBaseline(oldFields);

        return EventsProvider.dedupeFieldsByIdentifier(
            inFields.map((inField) => {
                const oldField = oldFields.find((of) => of.identifier === inField.identifier);
                let merged = oldField !== undefined
                    ? EventsProvider.mergeMaterialSubsectionWithOld(oldField, inField)
                    : EventsProvider.cloneDataSetFieldDeep(inField);

                if (structureBaseline !== undefined) {
                    merged = EventsProvider.mergeSubsectionStructure(structureBaseline, merged);
                }

                return merged;
            })
        );
    }

    private static getMaterialSubsectionBaseline(oldFields: DataSetField[]): DataSetField | undefined {
        let baseline: DataSetField | undefined;
        for (const field of oldFields) {
            if (field.fields && (!baseline || !baseline.fields || field.fields.length > baseline.fields.length)) {
                baseline = field;
            }
        }
        return baseline;
    }

    private static mergeSubsectionStructure(structureSource: DataSetField, subsection: DataSetField): DataSetField {
        const merged = EventsProvider.cloneDataSetFieldDeep(subsection);
        if (!structureSource.fields) {
            return merged;
        }
        if (!merged.fields) {
            merged.fields = [];
        }

        for (const templateNested of structureSource.fields) {
            const existing = merged.fields.find((f) => f.identifier === templateNested.identifier);
            if (existing === undefined) {
                const placeholder = EventsProvider.cloneDataSetFieldDeep(templateNested);
                placeholder.valueString = undefined;
                placeholder.valueNumber = undefined;
                placeholder.valueBool = undefined;
                merged.fields.push(placeholder);
            }
        }

        merged.fields.sort((lhs, rhs) => lhs.position - rhs.position);
        return merged;
    }

    /**
     * Unsynced rows can be sparse (only changed nested fields). Merge with the last
     * saved subsection so e.g. 89a5a1f3__0 still includes 67b53883 when only blend changed.
     */
    private static mergeMaterialSubsectionWithOld(oldSub: DataSetField, newSub: DataSetField): DataSetField {
        const merged = EventsProvider.cloneDataSetFieldDeep(oldSub);
        if (!newSub.fields) {
            return EventsProvider.cloneDataSetFieldDeep(newSub);
        }
        if (!merged.fields) {
            merged.fields = [];
        }

        for (const newNested of newSub.fields) {
            const target = merged.fields.find((f) => f.identifier === newNested.identifier);
            if (target !== undefined) {
                if (newNested.valueBool !== undefined) {
                    target.valueBool = newNested.valueBool;
                }
                if (newNested.valueNumber != null) {
                    target.valueNumber = newNested.valueNumber;
                }
                if (newNested.valueString != null) {
                    target.valueString = newNested.valueString;
                } else if (newNested.valueString === undefined && newNested.valueNumber == null && newNested.valueBool === undefined) {
                    target.valueString = undefined;
                    target.valueNumber = undefined;
                    target.valueBool = undefined;
                }
            } else {
                merged.fields.push(EventsProvider.cloneDataSetFieldDeep(newNested));
            }
        }

        return merged;
    }

    private static cloneDataSetFieldDeep(field: DataSetField): DataSetField {
        const clone = new DataSetField({
            position: field.position,
            identifier: field.identifier,
            valueString: field.valueString,
            valueNumber: field.valueNumber,
            valueBool: field.valueBool,
            changed: field.changed,
        });
        if (field.fields) {
            clone.fields = field.fields.map((subField) => EventsProvider.cloneDataSetFieldDeep(subField));
        }
        return clone;
    }

    private static dedupeFieldsByIdentifier(fields: DataSetField[]): DataSetField[] {
        const seen = new Set<string>();
        const result: DataSetField[] = [];
        for (const field of fields) {
            if (!field.identifier || seen.has(field.identifier)) {
                continue;
            }
            seen.add(field.identifier);
            result.push(field);
        }
        return result;
    }

    // tslint:disable-next-line: member-ordering
    public static filterFieldUpdates(oldDataSetField: DataSetField[] | undefined): any {
        return (inField: DataSetField) => {
            if (oldDataSetField === undefined) {
                return true;
            }
            const oldField = oldDataSetField.find((of) => of.identifier === inField.identifier);
            if (oldField === undefined && inField.fields) {
                return (inField.fields = inField.fields.map((o) => Object.assign({}, o)).filter(EventsProvider.filterField)).length;
            } else if (oldField === undefined) {
                return inField.valueBool !== undefined || inField.valueNumber !== undefined || inField.valueString !== undefined;
            }
            if (inField.fields) {
                // return inField.fields.filter(EventsProvider.filterField, this);
                return (inField.fields = inField.fields.map((o) => Object.assign({}, o)).filter(EventsProvider.filterFieldUpdates(oldField.fields))).length;
            } else if (inField.valueString
                && inField.valueString.match(/[0-9]{4}.[0-9]{2}.[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}.[0-9]{3,7}[Z]{0,1}/)
                && oldField.valueString) {
                // check for dates. ISO date strings from api can have some additional decimals for milliseconds
                return (new Date(inField.valueString)).toISOString() !== (new Date(oldField.valueString)).toISOString();
            } else if (inField.valueBool !== oldField.valueBool
                || inField.valueNumber !== oldField.valueNumber
                || inField.valueString !== oldField.valueString) {
                return true;
            }
            return false;
        };
    }

}

export default EventsProvider;
